import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FilterEnum, IActiveFilter, IActiveFilterNew } from '@app/models';
import { useTypedTranslation } from '@app/hooks';
import DOMPurify from 'dompurify';
import { DATE_FORMAT, interpolateTranslation, passlyFormatDate } from '@app/helpers';
import { Calendar } from '@itglue/kaseya-ui';
import moment from 'moment';

interface IPossibleFilter {
    value: string;
    label: string;
    name: string;
}

interface IFilterProps {
    className?: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    filterItems: IActiveFilterNew[];
    setFilters: (filters: IActiveFilter[]) => void;
    totalItemsCount?: number;
    currentCount?: number;
    defaultSearchBy: string;
    disabled?: boolean;
    hideCounter?: boolean;
}

interface IInnerFilterItem {
    filterBy?: string;
    value: string;
    label: string;
    name: string;
    suggestionsForFilter?: IPossibleFilter[];
}

const SEPARATOR = ':';
const FILTER_WRAPPER_CLASS = 'filter-bar__wrapper';

const FilterBar = ({
    className,
    filterItems,
    setFilters,
    totalItemsCount = 0,
    currentCount = 0,
    defaultSearchBy,
    disabled,
    hideCounter
}: IFilterProps) => {
    const { translation } = useTypedTranslation();

    const [inputValue, setInputValue] = useState('');
    const [filter, setFilter] = useState('');
    const [filterValue, setFilterValue] = useState('');
    const [date, setDate] = useState<Date[]>([]);

    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [isDropdownHovered, setIsDropdownHovered] = useState(false);
    const [filterSuggestions, setFilterSuggestions] = useState<IInnerFilterItem[]>([]);
    const [valueSuggestions, setValueSuggestions] = useState<IInnerFilterItem[]>([]);
    const [showDateRange, setShowDateRange] = useState(false);

    const [tags, setTags] = useState<IActiveFilter[]>([]);
    const inputRef = useRef<HTMLInputElement>(null);
    const handleWrapperClick = (e: any) => {
        const classList = e.target.classList;

        if (!classList.contains('datepicker__year') && !classList.contains('datepicker__month')) {
            inputRef.current?.focus();
        }
    };
    const allFilterItemsMemoized = useMemo(() => {
        return filterItems.map((item) => {
            const suggestionsForFilter = item.suggestionsForFilter?.map((suggestion) => ({
                name: '',
                value: suggestion.value,
                label: translation(suggestion.label)
            }));

            return {
                name: item.type,
                value: item.value,
                label: translation(item.label),
                filterBy: item.filterBy,
                ...(item.suggestionsForFilter && item.suggestionsForFilter.length > 0 && { suggestionsForFilter })
            };
        });
    }, [filterItems]);

    useEffect(() => {
        if (allFilterItemsMemoized.length) {
            setSuggestions(filter, '', inputValue);
        }
    }, [allFilterItemsMemoized]);

    const setSuggestions = (newFilter: string, newFilterValue: string, newInputValue: string, newTags?: IActiveFilter[]) => {
        const isFilterValueSetInInput = newInputValue.includes(SEPARATOR);
        const showAllFilterSuggestions = newInputValue.length === 0;

        if (showAllFilterSuggestions) {
            setFilterSuggestions(getFilterSuggestions('', newTags));
            setValueSuggestions([]);
        } else if (!isFilterValueSetInInput && newFilter) {
            setFilterSuggestions(getFilterSuggestions(newFilter));
            setValueSuggestions([]);
        } else if (isFilterValueSetInInput) {
            setFilterSuggestions([]);
            setValueSuggestions(getValueSuggestions(newFilter, newFilterValue));
        }
    };

    const getFilterSuggestions = (filter?: string, newTags?: IActiveFilter[]) => {
        return allFilterItemsMemoized.filter((item) => {
            const translatedLabel = item.label.toLowerCase();
            const tagsToUse = newTags?.length ? newTags : tags;
            const isTagExists = tagsToUse.some((tag) => tag.label === item.label);

            return filter ? translatedLabel.includes(filter.toLowerCase()) && !isTagExists : !isTagExists;
        });
    };

    const getValueSuggestions = (filter: string, filterValue: string) => {
        const foundedItem = allFilterItemsMemoized.find((item) => item.label.toLowerCase() === filter.toLowerCase());

        setShowDateRange(foundedItem?.name === FilterEnum.dateRange);

        return (
            foundedItem?.suggestionsForFilter
                ?.filter((suggestion) => !filterValue || suggestion.label.toLowerCase().includes(filterValue))
                .map((suggestion) => ({
                    name: foundedItem.name,
                    value: suggestion.value,
                    label: suggestion.label
                })) || []
        );
    };

    const underlineSubstring = (str: string) => {
        let substring = filter && !filterValue ? filter : filterValue;
        const regex = new RegExp(`(${substring})`, 'gi');

        return str.replace(regex, '<u>$1</u>');
    };

    const handleChange = (e: any) => {
        const value = e.target.value;
        const [filter, filterValue] = value.split(SEPARATOR).map((part: string) => part?.trim());

        setSuggestions(filter, filterValue, value);
        setFilter(filter?.trim() || '');
        setFilterValue(filterValue?.trim() || '');
        setInputValue(value);
    };

    const handleMouseEnter = () => setIsDropdownHovered(true);
    const handleMouseLeave = () => setIsDropdownHovered(false);

    const handleFocus = () => {
        setIsFocused(true);
    };

    const handleBlur = () => {
        if (!isDropdownHovered) {
            setIsFocused(false);
        }
    };

    const changeTags = (tags: IActiveFilter[]) => {
        setTags(tags);
        setFilters(tags);
    };

    const clearAll = () => {
        setShowDateRange(false);
        setFilter('');
        setFilterValue('');
        setInputValue('');
        changeTags([]);
    };

    const clearTag = (tag: IActiveFilter, index: number) => () => {
        const newTags = [...tags];

        newTags.splice(index, 1);
        changeTags(newTags);
    };

    const clearLastTag = () => {
        const newTags = [...tags];

        newTags.pop();
        changeTags(newTags);
    };

    const onKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && inputValue.length === 0) {
            clearLastTag();
            return;
        }

        if (e.key !== 'Enter') {
            return;
        }

        let newTags: IActiveFilter[] | undefined;

        if (filter && !filterValue) {
            // in there is no chosen filter search by displayName
            newTags = [
                ...tags,
                {
                    name: FilterEnum.contains,
                    label: translation('SEARCH'),
                    filterBy: defaultSearchBy,
                    value: inputValue,
                    displayValueName: inputValue
                }
            ];
            changeTags(newTags);
        } else {
            const foundedFilterItem = allFilterItemsMemoized.find((item) => item.label.toLowerCase() === filter.toLowerCase());

            if (foundedFilterItem) {
                const foundedValueItem = valueSuggestions.find((item) => item.label.toLowerCase() === filterValue.toLowerCase());
                const newTag = {
                    ...foundedFilterItem,
                    value: foundedValueItem ? foundedValueItem.value : filterValue,
                    displayValueName: filterValue
                };

                newTags = [...tags, { ...foundedFilterItem, ...newTag }];

                setInputValue(`${filter}: ${filterValue}`);
                changeTags(newTags);
            }
        }

        setIsDropdownHovered(false);
        setFilter('');
        setFilterValue('');
        setInputValue('');
        setSuggestions('', '', '', newTags);
    };

    useEffect(() => {
        if (date[1] === null) {
            return;
        }

        const label = passlyFormatDate(date[0], DATE_FORMAT) + ' - ' + passlyFormatDate(date[1], DATE_FORMAT);
        const foundedItem = allFilterItemsMemoized.find((item) => item.label.toLowerCase() === filter.toLowerCase());
        const value = '&after=' + moment(date[0]).startOf('day').toISOString() + '&before=' + moment(date[1]).endOf('day').toISOString();

        if (foundedItem) {
            changeTags([
                ...tags,
                {
                    name: foundedItem.name,
                    label: foundedItem.label,
                    filterBy: foundedItem.filterBy,
                    value: value,
                    displayValueName: label
                }
            ]);
        }

        setShowDateRange(false);
        setInputValue('');
        setFilter('');
        setFilterValue('');
    }, [date]);

    const onDropdownItemClick = (item: IInnerFilterItem) => () => {
        const needToSetValue = inputValue.includes(SEPARATOR);
        let filterNew = '';
        let inputValueNew = '';

        if (!needToSetValue) {
            filterNew = item.label;
            inputValueNew = `${filterNew}: `;
        } else {
            const foundedItem = allFilterItemsMemoized.find((item) => item.label.toLowerCase() === filter.toLowerCase());

            if (foundedItem) {
                changeTags([
                    ...tags,
                    {
                        name: foundedItem.name,
                        label: foundedItem.label,
                        filterBy: foundedItem.filterBy,
                        value: item.value,
                        displayValueName: item.label
                    }
                ]);
            }
        }

        setIsDropdownHovered(false);
        setInputValue(inputValueNew);
        setFilter(filterNew);
        setFilterValue('');
        setSuggestions(filterNew, '', inputValueNew);
    };

    const handleCalendar1Change = (e: any) => {
        setDate(e.value);
    };

    const renderDropdownItem = (item: IInnerFilterItem, index: number) => {
        if (filter || filterValue) {
            return (
                <div
                    key={index}
                    className='filter-bar__dropdown-item'
                    onClick={onDropdownItemClick(item)}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(underlineSubstring(item.label)) }}
                    data-testid='listItem'
                />
            );
        } else {
            return (
                <div key={index} className='filter-bar__dropdown-item' onClick={onDropdownItemClick(item)} data-testid='listItem'>
                    {item.label}
                </div>
            );
        }
    };

    return (
        <div className='filter-bar'>
            <div className={`${FILTER_WRAPPER_CLASS} ${className}`} onClick={handleWrapperClick}>
                <div className='filter-bar__tags'>
                    {tags?.map((tag, index) => (
                        <span className='filter-bar__tag' key={index}>
                            {`${tag.label}: ${tag.displayValueName}`}
                            <i className='filter-bar__tag-close fa-regular fa-xmark' onClick={clearTag(tag, index)} />
                        </span>
                    ))}
                    <div className='filter-bar__container'>
                        <input
                            type='text'
                            ref={inputRef}
                            value={inputValue}
                            onChange={handleChange}
                            onKeyDown={onKeyPress}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            placeholder={translation('FILTER_OR_SEARCH_PLACEHOLDER')}
                            className='filter-bar__input'
                            disabled={disabled}
                        />
                        {(isFocused && filterSuggestions.length) || valueSuggestions.length || showDateRange ? (
                            <div
                                className='filter-bar__dropdown'
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseLeave}
                                data-testid='dropdown'
                            >
                                <div className='filter-bar__dropdown-list'>
                                    {filterSuggestions.length > 0 ? (
                                        filterSuggestions.map(renderDropdownItem)
                                    ) : valueSuggestions.length > 0 ? (
                                        valueSuggestions.map(renderDropdownItem)
                                    ) : showDateRange ? (
                                        <Calendar
                                            name='dateRange'
                                            value={date?.length ? date : undefined}
                                            onChange={handleCalendar1Change}
                                            selectionMode='range'
                                            readOnlyInput={true}
                                            hideOnDateTimeSelect={true}
                                            showWeek={true}
                                            monthNavigator={true}
                                            yearNavigator={true}
                                            inline={true}
                                            showIcon={false}
                                            yearRange={'2020:2100'}
                                        />
                                    ) : (
                                        <div>{translation('NO_MATCHING_RESULTS')}</div>
                                    )}
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>

                {inputValue || tags.length ? (
                    <i className='filter-bar__clear-button fa-regular fa-circle-xmark' onClick={clearAll} data-testid='clearButton' />
                ) : null}
            </div>
            {hideCounter ? null : (
                <div
                    className='filter-bar__info'
                    dangerouslySetInnerHTML={{
                        __html: interpolateTranslation(translation('TABLE_FILTER_RESULTS'), [String(currentCount), String(totalItemsCount)])
                    }}
                />
            )}
        </div>
    );
};

export default FilterBar;
